import { FC } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { Address } from '@weave/schema-gen-ts/dist/schemas/phone/address/v1/address_service.pb';
import { stateOptions, CountryCodes, countryOptions } from '@frontend/geography';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  ContentLoader,
  DropdownField,
  FormRow,
  Modal,
  PostalCodeField,
  TextField,
  Text,
  InlineNotification,
  TextLink,
  useModalControl,
} from '@frontend/design-system';
import { findMatchingE911Address, formatE911AddressForDisplay } from '../../../utils/e911-address';
import { useAddressForm } from './use-e911-address-form';

type AddressSaveErrorVariant = 'invalid-address' | 'generic';

export type AddressSaveError = {
  key: number;
  variant: AddressSaveErrorVariant;
};

interface Props {
  address?: Address;
  onBack?: () => void;
  onCancel?: () => void;
  onSave: (address: Address) => void;
  isForm?: boolean;
  isLoading?: boolean;
  footerStyles?: Interpolation<Theme>;
  addressSaveError?: AddressSaveError;
  clearAddressSaveError: () => void;
  addresses?: Address[];
  onUseExistingAddress?: (address: Address) => void;
}

const WEAVE_SUPPORT_LINK = 'https://www.weavehelp.com/hc/en-us/p/contact-us';

export const E911AddressForm: FC<React.PropsWithChildren<Props>> = ({
  address,
  onBack,
  onCancel,
  onSave,
  isForm = true,
  isLoading,
  footerStyles,
  addressSaveError,
  clearAddressSaveError,
  addresses = [],
  onUseExistingAddress,
}) => {
  const handleSave = () => {
    onSave({ id: address?.id, ...values } as Address);
  };

  const { getFieldProps, formProps, getErrors, values } = useAddressForm({
    address,
    onSubmit: handleSave,
  });
  const duplicateAddress = findMatchingE911Address(addresses, values, address?.id);
  const getFieldPropsWithErrorClear = <FieldName extends Parameters<typeof getFieldProps>[0]>(fieldName: FieldName) => {
    const fieldProps = getFieldProps(fieldName);

    return {
      ...fieldProps,
      onChange: (event: Parameters<typeof fieldProps.onChange>[0]) => {
        clearAddressSaveError();
        fieldProps.onChange(event);
      },
    };
  };

  const props = {
    onBack,
    onCancel,
    getErrors,
    getFieldProps: getFieldPropsWithErrorClear,
    isForm,
    footerStyles,
    addressSaveError,
    duplicateAddress,
    onUseExistingAddress,
    formProps,
  };

  const Container = isForm ? (
    <form css={formStyles} {...formProps}>
      <E911FormFields {...props} />
    </form>
  ) : (
    <div css={formStyles}>
      <E911FormFields onSave={handleSave} {...props} />
    </div>
  );

  return (
    <>
      <ContentLoader show={isLoading} />
      {Container}
    </>
  );
};

const E911FormFields = ({
  getFieldProps,
  getErrors,
  onBack,
  onCancel,
  onSave,
  isForm,
  footerStyles,
  addressSaveError,
  duplicateAddress,
  onUseExistingAddress,
}: {
  getErrors: ReturnType<typeof useAddressForm>['getErrors'];
  getFieldProps: ReturnType<typeof useAddressForm>['getFieldProps'];
  onBack?: () => void;
  onCancel?: () => void;
  onSave?: (address?: Address) => void;
  isForm: boolean;
  footerStyles?: Interpolation<Theme>;
  addressSaveError?: AddressSaveError;
  duplicateAddress?: Address;
  onUseExistingAddress?: (address: Address) => void;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'e911' });
  const addressVerificationModal = useModalControl();
  const countryProps = getFieldProps('country');

  const warningVariant = duplicateAddress ? 'duplicate' : addressSaveError?.variant;

  const openContactSupportClick = () => {
    window.open(WEAVE_SUPPORT_LINK, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {warningVariant && (
        <FormRow>
          <InlineNotification status='warn'>
            <div>
              <Text size='medium'>
                {warningVariant === 'duplicate' ? (
                  duplicateAddress ? (
                    <>
                      {t('Address already exists.')}{' '}
                      {onUseExistingAddress ? (
                        <TextLink as='span' onClick={() => onUseExistingAddress(duplicateAddress)}>
                          {t('Use {{name}} {{address}}?', {
                            name: duplicateAddress.name,
                            address: formatE911AddressForDisplay(duplicateAddress),
                          })}
                        </TextLink>
                      ) : (
                        t('({{name}} {{address}})', {
                          name: duplicateAddress.name,
                          address: formatE911AddressForDisplay(duplicateAddress),
                        })
                      )}
                    </>
                  ) : null
                ) : warningVariant === 'invalid-address' ? (
                  <Trans t={t}>
                    Address could not be verified. Try fixing your address.{' '}
                    <TextLink as='span' onClick={addressVerificationModal.openModal}>
                      I think my address is correctly inputted.
                    </TextLink>
                  </Trans>
                ) : (
                  t('Address Creation failed. Please try again.')
                )}
              </Text>
            </div>
          </InlineNotification>
        </FormRow>
      )}
      <FormRow
        css={css`
          margin-top: var(--fabric-space-gap-xs);
        `}
      >
        <TextField {...getFieldProps('name')} label={t('Address Name')} />
      </FormRow>
      <FormRow>
        <DropdownField {...getFieldProps('country')} label={t('Country')}>
          {countryOptions.map(({ label, value }) => (
            <DropdownField.Option key={value} value={value}>
              {label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </FormRow>
      <FormRow>
        <TextField {...getFieldProps('street')} label={t('Street Address')} />
      </FormRow>
      <FormRow>
        <TextField {...getFieldProps('streetSecondary')} label={t('Address Line 2')} />
      </FormRow>
      <FormRow>
        <TextField {...getFieldProps('city')} label={t('City')} />
      </FormRow>
      <FormRow>
        <DropdownField
          {...getFieldProps('state')}
          label={countryProps.value === CountryCodes.USA ? t('State') : t('Province')}
        >
          {stateOptions[countryProps.value as CountryCodes]?.map(({ label, value }) => (
            <DropdownField.Option key={value} value={value} searchValue={label}>
              {label}
            </DropdownField.Option>
          ))}
        </DropdownField>
        <PostalCodeField {...getFieldProps('postalCode')} label={t('Postal Code')} />
      </FormRow>
      <Modal.Footer
        css={footerStyles}
        primary={{
          label: t('Save'),
          onClick: () => {
            isForm ? 'submit' : onSave?.();
          },
          disabled:
            warningVariant === 'invalid-address' ||
            warningVariant === 'duplicate' ||
            Object.entries(getErrors()).length > 0,
        }}
        secondary={{
          label: t('Cancel'),
          onClick: onCancel,
        }}
        tertiary={{
          label: onBack && (
            <div css={{ display: 'flex', gap: theme.spacing(1) }}>
              <Icon name='back' />
              <Text color='primary' weight='bold'>
                {t('Back')}
              </Text>
            </div>
          ),
          onClick: onBack && onBack,
        }}
      />
      <Modal {...addressVerificationModal.modalProps} maxWidth={400}>
        <Modal.Header onClose={addressVerificationModal.closeModal}>{t('Couldn’t Verify Address')}</Modal.Header>
        <Modal.Body>
          <Text>
            {t(
              "We couldn't verify this address for E911 service. This can happen with new construction, recent address changes, or some rural locations."
            )}
          </Text>
          <Text>
            <Trans t={t}>
              If you're confident the address is correct,{' '}
              <TextLink css={{ textDecoration: 'underline' }} onClick={openContactSupportClick}>
                Contact Support
              </TextLink>
              . They can work with our E911 provider to verify or add your emergency address.
            </Trans>
          </Text>
        </Modal.Body>
        <Modal.Footer
          primary={{
            label: t('Okay'),
            onClick: addressVerificationModal.closeModal,
          }}
        />
      </Modal>
    </>
  );
};

const formStyles = css`
  & > div:last-of-type {
    padding: 0;
  }
`;
